import React from "react";
import { Dialog } from "@mui/material";
import styled from "styled-components";
import { portalColors } from "./BusinessPortalComponents";
import Button from "./Button";
import ArrowDownSignToNavigate from "src/img/arrow-down-sign-to-navigate.svg";

export const standardModalWidths = {
    small: "min(28rem, calc(100vw - 2rem))",
    medium: "min(42rem, calc(100vw - 2rem))",
    large: "min(52rem, calc(100vw - 2rem))",
    compactWide: "min(58rem, calc(100vw - 2rem))",
    wide: "min(64rem, calc(100vw - 2rem))",
    full: "min(76rem, calc(100vw - 2rem))",
    compactTable: "min(58rem, calc(100vw - 2rem))",
    table: "min(86rem, calc(100vw - 2rem))",
};

const materialMaxWidthMap = {
    xs: standardModalWidths.small,
    sm: standardModalWidths.medium,
    md: standardModalWidths.large,
    lg: standardModalWidths.wide,
    xl: standardModalWidths.full,
};

export const getStandardModalWidth = (widthMode = "medium", maxWidth) => {
    if (standardModalWidths[widthMode]) {
        return standardModalWidths[widthMode];
    }

    if (materialMaxWidthMap[maxWidth]) {
        return materialMaxWidthMap[maxWidth];
    }

    return widthMode || standardModalWidths.medium;
};

export const getStandardModalPaperStyle = ({ widthMode = "medium", maxWidth, isDarkMode = false } = {}) => ({
    width: getStandardModalWidth(widthMode, maxWidth),
    height: "auto",
    maxWidth: "calc(100vw - 2rem)",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    overflow: "hidden",
    background: isDarkMode ? "var(--calendar-bg)" : portalColors.surface,
    color: portalColors.text,
    boxShadow: "0 28px 70px rgba(0, 0, 0, 0.28)",
    border: `1px solid ${isDarkMode ? "var(--calendar-border-strong)" : portalColors.borderStrong}`,
    animation: "standard-modal-panel-enter 0.18s ease both",
});

export const StandardModalFrame = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: inherit;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overflow-x: hidden;
    background: ${p => p.$isDarkMode
        ? "linear-gradient(180deg, var(--calendar-shell) 0%, var(--calendar-surface) 100%)"
        : `linear-gradient(180deg, ${portalColors.surface} 0%, ${portalColors.surfaceSoft} 100%)`};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
`;

export const StandardModalHeader = styled.div`
    min-width: 0;
    max-width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem 1.6rem 1.2rem;
    border-bottom: 1px solid ${p => p.$isDarkMode ? "var(--calendar-border)" : portalColors.border};
    background: transparent;
    box-sizing: border-box;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        padding: 1.25rem;
    }
`;

export const StandardModalTitleBlock = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.45rem;
`;

export const StandardModalEyebrow = styled.div`
    color: ${p => p.color || portalColors.text};
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    line-height: 1.2;
    text-transform: uppercase;
`;

export const StandardModalHeading = styled.h2`
    color: ${p => p.color || portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "1.75rem"};
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: 0;
    margin: 0;
    text-align: ${p => p.align || "left"};
    overflow-wrap: anywhere;

    @media (max-width: 640px) {
        font-size: 1.5rem;
    }
`;

export const StandardModalDescription = styled.p`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    font-weight: 500;
    line-height: 1.5;
    margin: 0;
    max-width: ${p => p.maxWidth || "44rem"};
    text-align: ${p => p.align || "left"};
    overflow-wrap: anywhere;
`;

export const StandardModalOptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const StandardModalOptionCard = styled.div`
    min-width: 0;
    background: ${portalColors.surface};
    border: 1px solid ${p => p.$hasError ? portalColors.danger : portalColors.border};
    border-radius: 8px;
    opacity: 1;

    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    text-align: center;
`;

export const StandardModalOptionCardText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const StandardModalOptionCardTitle = styled.h3`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.5;
    margin: 0;
    text-align: left;
    overflow-wrap: anywhere;
`;

export const StandardModalOptionCardActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
`;

export const StandardModalDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${p => p.color || portalColors.border};
    flex: 0 0 auto;
`;

export const StandardModalBody = styled.div`
    width: 100%;
    max-width: 100%;
    min-height: 0;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${p => p.padding || "1.2rem 1.6rem 1.5rem"};
    display: grid;
    gap: ${p => p.gap || "1.1rem"};
    box-sizing: border-box;

    > * {
        min-width: 0;
        max-width: 100%;
    }

    @media (max-width: 640px) {
        padding: ${p => p.padding === "0" ? "0" : "1.25rem"};
    }
`;

export const StandardModalFixedLayout = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: 100%;
    max-height: inherit;
    min-height: 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
`;

export const StandardModalScrollArea = styled.div`
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 0;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${p => p.padding || "1.2rem 1.6rem 1.5rem"};
    box-sizing: border-box;
`;

export const StandardModalFooter = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: ${p => p.justifyContent || "flex-end"};
    gap: 0.75rem;
    padding: 1rem 1.6rem 1.35rem;
    border-top: 1px solid ${p => p.$isDarkMode ? "var(--calendar-border)" : portalColors.border};
    background: transparent;
    flex-wrap: wrap;

    @media (max-width: 640px) {
        align-items: stretch;
        flex-direction: column;
        padding: 1rem 1.25rem 1.25rem;
    }
`;

export const StandardModalActionGroup = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${p => p.justifyContent || "flex-end"};
    gap: 0.75rem;
    margin-left: ${p => p.$pushRight || p.$center ? "auto" : "0"};
    margin-right: ${p => p.$center ? "auto" : "0"};

    @media (max-width: 640px) {
        width: 100%;
        margin-left: 0;

        > *,
        button {
            width: 100%;
            max-width: 100%;
        }
    }
`;

export const StandardModalActionButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${p => p.width || "8.75rem"};
    min-width: ${p => p.minWidth || "8.75rem"};
    height: ${p => p.height || "2.625rem"};
    min-height: ${p => p.minHeight || "2.625rem"};
    padding: ${p => p.padding || "0 1rem"};
    box-sizing: border-box;
    border: ${p => p.border || (
        p.$variant === "primary"
            ? `1px solid ${portalColors.blue}`
            : p.$variant === "danger"
                ? "1px solid #D64545"
                : `1px solid ${portalColors.text}`
    )};
    border-radius: ${p => p.borderRadius || "7px"} !important;
    background-color: ${p => p.backgroundColor || (
        p.$variant === "primary"
            ? portalColors.blue
            : p.$variant === "danger"
                ? "#D64545"
                : portalColors.surface
    )};
    color: ${p => p.color || (p.$variant === "primary" || p.$variant === "danger" ? portalColors.actionPrimaryText : portalColors.text)};
    box-shadow: none;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    font-weight: ${p => p.weight || 700};
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};

    @media (max-width: 640px) {
        width: 100%;
        max-width: 100%;
    }
`;

export const StandardModalCloseButton = styled.button`
    width: 2.35rem;
    height: 2.35rem;
    border: 1px solid ${portalColors.border};
    border-radius: 999px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    line-height: 1;
    transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;

    &:hover {
        background: ${portalColors.surface};
        border-color: ${portalColors.borderStrong};
        transform: translateY(-1px);
    }
`;

export const StandardModalText = styled.div`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.9375rem"};
    font-weight: ${p => p.weight || 500};
    line-height: ${p => p.lineHeight || 1.55};
    margin: ${p => p.margin || "0"};
    padding: ${p => p.padding || "0"};
    text-align: ${p => p.align || "left"};
    overflow-wrap: anywhere;
`;

export const StandardModalSection = styled.section`
    min-width: 0;
    display: grid;
    align-content: ${p => p.alignContent || "normal"};
    gap: ${p => p.gap || "0.9rem"};
    padding: ${p => p.padding || "1rem"};
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: 8px;
    box-sizing: border-box;
`;

export const StandardModalSectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

export const StandardModalSectionTitle = styled.div`
    color: ${p => p.color || portalColors.textSoft};
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1.25;
    text-transform: uppercase;
`;

export const StandardModalFormGrid = styled.div`
    display: grid;
    grid-template-columns: ${p => typeof p.columns === "string"
        ? p.columns
        : `repeat(${p.columns || 2}, minmax(0, 1fr))`};
    gap: ${p => p.gap || "0.85rem"};

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const StandardModalFormGroup = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.5rem;
`;

export const StandardModalLabel = styled.label`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
`;

export const StandardModalHelperText = styled.div`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.45;
`;

export const StandardModalErrorText = styled(StandardModalHelperText)`
    color: ${portalColors.danger};
`;

export const StandardModalInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 46px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${p => p.background || portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0 1rem;
    outline: none;

    ::placeholder {
        color: ${portalColors.textSoft};
        font-weight: 500;
    }

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const StandardModalTextarea = styled.textarea`
    width: 100%;
    min-width: 0;
    min-height: 120px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    padding: 0.9rem 1rem;
    resize: vertical;
    outline: none;

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const StandardModalSelect = styled.select`
    width: ${p => p.width || "100%"};
    min-width: 0;
    height: 46px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    padding: 0 2.6rem 0 1rem;
    outline: none;
    appearance: none;
    background-image: url("${ArrowDownSignToNavigate}");
    background-repeat: no-repeat;
    background-position: right 0.85rem center;
    background-size: 1rem 1rem;

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const StandardModalInfoCard = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.35rem;
    padding: ${p => p.padding || "0.85rem"};
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: 8px;
    color: ${portalColors.text};
    box-sizing: border-box;
`;

export const StandardModalTableSection = styled.div`
    min-width: 0;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surface};
    overflow: hidden;
`;

export const StandardModalTableScroll = styled.div`
    min-width: 0;
    max-height: ${p => p.maxHeight || "24rem"};
    overflow: auto;
`;

export const StandardModalTable = styled.table`
    width: 100%;
    min-width: ${p => p.minWidth || "0"};
    border-collapse: collapse;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;

    th,
    td {
        padding: 0.75rem 0.9rem;
        border-bottom: 1px solid ${portalColors.border};
        text-align: left;
        vertical-align: middle;
    }

    th {
        position: sticky;
        top: 0;
        z-index: 1;
        background: ${portalColors.surfaceSoft};
        color: ${portalColors.text};
        font-size: 0.75rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    tr:last-child td {
        border-bottom: none;
    }
`;

export const StandardModalState = styled.div`
    min-height: ${p => p.minHeight || "8rem"};
    display: grid;
    place-items: center;
    gap: 0.5rem;
    text-align: center;
    padding: 1.5rem;
    background: ${portalColors.surfaceSoft};
    border: ${p => p.border || `1px dashed ${portalColors.borderStrong}`};
    border-radius: 8px;
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    line-height: 1.5;
`;

export function StandardModal({
    open,
    show = undefined,
    onClose,
    widthMode = "medium",
    maxWidth = undefined,
    isDarkMode = false,
    title = undefined,
    eyebrow = undefined,
    description = undefined,
    headerAside = undefined,
    footer = undefined,
    children,
    hideCloseButton = false,
    bodyPadding = undefined,
    bodyGap = undefined,
    paperStyle = undefined,
    BackdropProps = undefined,
    ...dialogProps
}) {
    const isOpen = show !== undefined ? show : open;

    return (
        <Dialog
            open={Boolean(isOpen)}
            onClose={onClose}
            maxWidth={false}
            scroll="paper"
            aria-labelledby="standard-modal-title"
            aria-describedby="standard-modal-description"
            disableEnforceFocus
            disableScrollLock
            BackdropProps={{
                style: {
                    backgroundColor: "rgba(15, 23, 42, 0.48)",
                    backdropFilter: "blur(2px)",
                },
                ...BackdropProps,
            }}
            PaperProps={{
                style: {
                    ...getStandardModalPaperStyle({ widthMode, maxWidth, isDarkMode }),
                    ...paperStyle,
                },
            }}
            {...dialogProps}
        >
            <style>
                {`
                    @keyframes standard-modal-panel-enter {
                        from {
                            opacity: 0;
                            transform: translateY(8px) scale(0.99);
                        }

                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .MuiDialog-paper {
                            animation: none !important;
                        }
                    }
                `}
            </style>
            <StandardModalFrame $isDarkMode={isDarkMode}>
                {(title || description || eyebrow || headerAside || (!hideCloseButton && onClose)) && (
                    <StandardModalHeader $isDarkMode={isDarkMode}>
                        <StandardModalTitleBlock>
                            {eyebrow && <StandardModalEyebrow>{eyebrow}</StandardModalEyebrow>}
                            {title && <StandardModalHeading id="standard-modal-title">{title}</StandardModalHeading>}
                            {description && (
                                <StandardModalDescription id="standard-modal-description">
                                    {description}
                                </StandardModalDescription>
                            )}
                        </StandardModalTitleBlock>
                        {headerAside || (!hideCloseButton && onClose && (
                            <StandardModalCloseButton type="button" onClick={onClose} aria-label="Close modal">
                                ×
                            </StandardModalCloseButton>
                        ))}
                    </StandardModalHeader>
                )}
                { children && <StandardModalBody padding={bodyPadding} gap={bodyGap}>
                    {children}
                </StandardModalBody>}
                {footer && (
                    <StandardModalFooter $isDarkMode={isDarkMode}>
                        {footer}
                    </StandardModalFooter>
                )}
            </StandardModalFrame>
        </Dialog>
    );
}

export default StandardModal;
